import React, { useEffect, useState } from 'react';
import { 
  Dialog, 
  DialogTitle, 
  DialogContent, 
  DialogActions, 
  Button, 
  Box, 
  Typography, 
  Avatar,
  IconButton,
  CircularProgress
} from '@mui/material';
import { styled } from '@mui/material/styles';
import ReactMarkdown from 'react-markdown';
import DOMPurify from 'dompurify';
import PersonIcon from '@mui/icons-material/Person';
import CloseIcon from '@mui/icons-material/Close';
import useTheme from '../hooks/useTheme';

const StyledDialog = styled(Dialog)(({ theme }) => ({
  '& .MuiDialog-paper': {
    borderRadius: 12,
    maxWidth: '90vw',
    maxHeight: '90vh',
    width: '100%',
    margin: 16,
  },
}));

const ReportContent = styled(Box)(({ theme }) => ({
  maxHeight: '60vh',
  overflowY: 'auto',
  padding: theme.spacing(2),
  '& h1, & h2, & h3, & h4, & h5, & h6': {
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(1),
    fontWeight: 600,
  },
  '& p': {
    marginBottom: theme.spacing(1),
    lineHeight: 1.6,
  },
  '& ul, & ol': {
    marginBottom: theme.spacing(1),
    paddingLeft: theme.spacing(3),
  },
  '& li': {
    marginBottom: theme.spacing(0.5),
  },
}));

export default function ReportModal({ open, onClose, userEmail, reportDate }) {
  const theme = useTheme();
  const [user, setUser] = useState({ name: '', report: '', role: '', status: '' });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (open && userEmail && reportDate) {
      fetchReport();
    }
  }, [open, userEmail, reportDate]);

  const fetchReport = async () => {
    setLoading(true);
    setError('');
    try {
      const res = await fetch(`https://api.uxhq.com/by-email?email=${encodeURIComponent(userEmail)}&created_at=${reportDate}`);
      if (!res.ok) throw new Error('Report not found');
      const data = await res.json();
      
      // Clean up markdown backticks if present
      let report = data.report;
      if (typeof report === 'string') {
        // Remove only the first ```markdown or ``` and the last ```
        report = report.replace(/^```markdown\s*\n?/, '').replace(/^```\s*\n?/, '').replace(/```$/, '').trim();
      }
      
      setUser({ 
        name: data.user_info.name, 
        report,
        role: data.user_info.role,
        status: data.user_info.status
      });
    } catch (err) {
      setError('Could not load report.');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setUser({ name: '', report: '', role: '', status: '' });
    setError('');
    onClose();
  };

  return (
    <StyledDialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'space-between',
        borderBottom: `1px solid ${theme.borderColor}`,
        pb: 2
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Avatar sx={{ width: 40, height: 40, background: '#e3e7ed', color: '#b0b7c3' }}>
            <PersonIcon />
          </Avatar>
          <Box>
            <Typography variant="h6" fontWeight={700} sx={{ color: theme.textColor }}>
              {user.name || 'Report'}
            </Typography>
            {user.role && (
              <Typography variant="body2" sx={{ color: theme.textSecondaryColor, fontSize: 12 }}>
                {user.role} {user.status && `• ${user.status}`}
              </Typography>
            )}
            {reportDate && (
              <Typography variant="body2" sx={{ color: theme.textSecondaryColor, fontSize: 12 }}>
                {new Date(reportDate).toLocaleDateString('en-GB', { 
                  day: '2-digit', 
                  month: 'short', 
                  year: '2-digit'
                })}
              </Typography>
            )}
          </Box>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <img 
              src="/Group 37387.png" 
              alt="UXHQ Logo" 
              style={{ 
                width: 80, 
                height: 80,
                objectFit: 'contain'
              }}
            />
          </Box>
          <IconButton onClick={handleClose} size="small">
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>
      
      <DialogContent sx={{ p: 0 }}>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        ) : error ? (
          <Box sx={{ p: 3, textAlign: 'center' }}>
            <Typography color="error">{error}</Typography>
          </Box>
        ) : (
          <ReportContent>
            {user.report ? (
              <div 
                dangerouslySetInnerHTML={{ 
                  __html: DOMPurify.sanitize(user.report) 
                }}
                style={{
                  color: theme.textColor,
                  lineHeight: 1.6,
                  '& h1': { fontSize: '2em', fontWeight: 600, marginBottom: '0.5em' },
                  '& h2': { fontSize: '1.5em', fontWeight: 600, marginBottom: '0.4em' },
                  '& h3': { fontSize: '1.3em', fontWeight: 600, marginBottom: '0.3em' },
                  '& p': { marginBottom: '1em' },
                  '& ul, & ol': { marginBottom: '1em', paddingLeft: '1.5em' },
                  '& li': { marginBottom: '0.5em' }
                }}
              />
            ) : (
              <Typography sx={{ color: theme.textSecondaryColor, textAlign: 'center', py: 4 }}>
                No report found.
              </Typography>
            )}
          </ReportContent>
        )}
      </DialogContent>
      
      <DialogActions sx={{ p: 2, borderTop: `1px solid ${theme.borderColor}` }}>
        <Button onClick={handleClose} variant="outlined">
          Close
        </Button>
      </DialogActions>
    </StyledDialog>
  );
} 